import { useQuery } from '@apollo/client';
import { createError } from '@common/neo.tax';

import {
  Conditions,
  GET_RD_PROJECTS_SUMMARY,
  GetRdProjectsSummaryQuery,
  GetRdProjectsSummaryQueryVariables,
  keywordFilter,
  useSticky,
} from '@neo.tax/shared';

import { useRdContext } from '../context';
import { RdProjectFilterableColumn } from '../types';
import { mapRdProjectsSummaryFilterConditions } from '../utils';

export type RdProjectsSummary = NonNullable<GetRdProjectsSummaryQuery['getRdProjectsSummary']>;

type UseRdProjectsSummaryProps = {
  filterConditions?: Conditions<RdProjectFilterableColumn>;
  searchKeyword?: string | null;
};

type UseRdProjectsSummaryResult =
  | {
      loading: true;
      rdProjectsSummary: null;
    }
  | {
      loading: false;
      rdProjectsSummary: RdProjectsSummary;
    };

export const useRdProjectsSummary = ({
  filterConditions,
  searchKeyword,
}: UseRdProjectsSummaryProps = {}): UseRdProjectsSummaryResult => {
  const { fiscalYearId } = useRdContext();

  const {
    data: requestData,
    error,
    loading,
  } = useQuery<GetRdProjectsSummaryQuery, GetRdProjectsSummaryQueryVariables>(GET_RD_PROJECTS_SUMMARY, {
    variables: {
      fiscalYearId,
      filter: {
        and: [keywordFilter(['name'], searchKeyword), mapRdProjectsSummaryFilterConditions(filterConditions)],
      },
    },
  });

  // Keep existing data on the screen while a new query is running, so it
  // doesn't keep clearing the screen as you type into the filter.
  const stickyData = useSticky(requestData);
  const rdProjectsSummary = stickyData?.getRdProjectsSummary ?? null;

  if (loading) {
    return {
      loading: true,
      rdProjectsSummary: null,
    };
  }
  if (error) {
    throw error;
  }

  if (!stickyData || !rdProjectsSummary) {
    throw createError('MissingRdProjectsSummary', 'No data returned from getRdProjectsSummary query');
  }

  return {
    loading,
    rdProjectsSummary,
  };
};
